import React, { useState, useEffect } from "react";
import ABSDark from "../../assets/ABS-Logo/darkTheme/ABS-Dark.png";
import ABSLight from "../../assets/ABS-Logo/lightTheme/ABS-Light.png";

// Detect system color scheme preference
const getSystemTheme = () => {
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
};

const SplashScreen = ({ onComplete }) => {
  // Use system theme directly, don't wait for useTheme hook
  const [theme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme || getSystemTheme();
  });

  const [isVisible, setIsVisible] = useState(true);
  const [logoLoaded, setLogoLoaded] = useState(false);

  const isDarkMode = theme === "dark";
  const bgColor = "var(--bg)";
  const mainLogo = isDarkMode ? ABSDark : ABSLight;

  useEffect(() => {
    // Set body and html background immediately to prevent flash
    document.body.style.backgroundColor = bgColor;
    document.documentElement.style.backgroundColor = bgColor;

    // Apply theme class immediately
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.add("light");
      document.documentElement.classList.remove("dark");
    }

    // Fade out after 2 seconds, then call onComplete after fade transition
    const fadeTimer = setTimeout(() => {
      setIsVisible(false);
    }, 2000);

    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2500); // 2s + 0.5s fade out

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, bgColor, isDarkMode]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: bgColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.5s ease-out",
      }}
    >
      {/* Animated Background Gradient */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: isDarkMode
            ? "radial-gradient(circle at 50% 50%, rgba(230, 128, 5, 0.08) 0%, transparent 50%)"
            : "radial-gradient(circle at 50% 50%, rgba(230, 128, 5, 0.05) 0%, transparent 50%)",
          animation: "pulse 3s ease-in-out infinite",
        }}
      />

      {/* Main Content */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2rem",
        }}
      >
        {/* Logo Container with Glow Effect */}
        <div
          style={{
            position: "relative",
            animation:
              "logoEntry 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
            opacity: 0,
          }}
        >
          {/* Glow Effect Behind Logo */}
          <div
            style={{
              position: "absolute",
              inset: "-15px",
              background: isDarkMode
                ? "radial-gradient(circle, rgba(230, 128, 5, 0.12) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(230, 128, 5, 0.08) 0%, transparent 70%)",
              filter: "blur(15px)",
              animation: "glow 2.5s ease-in-out infinite alternate",
              opacity: logoLoaded ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />

          {/* Main Logo */}
          <img
            src={mainLogo}
            alt="ABS Logo"
            onLoad={() => setLogoLoaded(true)}
            style={{
              position: "relative",
              height: "clamp(80px, 12vw, 120px)",
              width: "auto",
              maxWidth: "80vw",
              filter: "drop-shadow(0 8px 20px rgba(0, 0, 0, 0.15))",
            }}
          />
        </div>

        {/* Welcome Text */}
        <div
          style={{
            animation: "textEntry 0.8s ease-out 0.4s forwards",
            opacity: 0,
            textAlign: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "clamp(1rem, 2.5vw, 1.5rem)",
              fontWeight: 500,
              color: "var(--muted)",
              letterSpacing: "0.03em",
            }}
          >
            Dev Team Portal
          </h2>
        </div>

        {/* Loading Progress Bar */}
        <div
          style={{
            width: "180px",
            height: "2px",
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.08)",
            borderRadius: "10px",
            overflow: "hidden",
            animation: "fadeIn 0.8s ease-out 0.6s forwards",
            opacity: 0,
          }}
        >
          <div
            style={{
              height: "100%",
              width: "100%",
              background: "var(--grad)",
              backgroundSize: "200% 100%",
              animation:
                "progressBar 2s ease-in-out forwards, shimmer 1.5s linear infinite",
              borderRadius: "10px",
              boxShadow: isDarkMode
                ? "0 0 8px rgba(230, 128, 5, 0.4)"
                : "0 0 6px rgba(230, 128, 5, 0.25)",
            }}
          />
        </div>
      </div>

      <style>
        {`
          @keyframes logoEntry {
            0% {
              opacity: 0;
              transform: scale(0.5) translateY(-30px);
            }
            100% {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          @keyframes textEntry {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }

          @keyframes glow {
            0% {
              opacity: 0.5;
              transform: scale(0.95);
            }
            100% {
              opacity: 1;
              transform: scale(1.05);
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
          }

          @keyframes progressBar {
            0% {
              transform: translateX(-100%);
            }
            100% {
              transform: translateX(0);
            }
          }

          @keyframes shimmer {
            0% {
              background-position: 200% center;
            }
            100% {
              background-position: -200% center;
            }
          }
        `}
      </style>
    </div>
  );
};

export default SplashScreen;
